import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Business,
  Security,
  Speed,
  SupportAgent,
  CheckCircle,
  ArrowForward,
  Cloud,
  Devices,
  Public,
  Analytics,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const EnterpriseSolutions = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const solutions = [
    { icon: <Cloud />, title: 'Global MPLS Network', desc: 'Connect all your offices worldwide with private, secure connectivity' },
    { icon: <Devices />, title: 'Unified Communications', desc: 'Voice, video, and messaging integrated into one platform' },
    { icon: <Public />, title: 'IoT Connectivity', desc: 'Global SIM cards and device management platform' },
    { icon: <Analytics />, title: 'SD-WAN Services', desc: 'Intelligent traffic routing and optimized cloud connectivity' },
  ];

  const benefits = [
    'Dedicated account team with 24/7 enterprise support',
    'Custom SLAs tailored to your business needs',
    'Priority network routing for mission-critical applications',
    'Advanced DDoS protection and threat monitoring',
    'Compliance-ready solutions (GDPR, HIPAA, PCI-DSS)',
    'Flexible contract terms and pricing models',
  ];

  return (
    <Box>
      <PageHeader 
        title="Enterprise Solutions" 
        subtitle="Custom communication infrastructure for global enterprises"
      />

      {/* Hero Stats */}
      <Box sx={{ py: 6, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {[
              { number: '500+', label: 'Enterprise Clients', color: '#0066CC' },
              { number: '150+', label: 'Countries Served', color: '#00A3FF' },
              { number: '99.999%', label: 'Uptime SLA', color: '#00C853' },
              { number: '24/7', label: 'Premium Support', color: '#FF6B00' },
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Solutions Grid */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Enterprise Solutions" 
                  sx={{ mb: 2, bgcolor: theme.palette.primary.light + '20', color: theme.palette.primary.main }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
                  Tailored Solutions for Global Enterprises
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  We understand that every enterprise has unique requirements. Our team works 
                  closely with you to design, deploy, and manage a communication infrastructure 
                  that scales with your business.
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {solutions.map((solution, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Stack direction="row" spacing={2}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>{solution.icon}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {solution.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {solution.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1551434678-e076c2236a9b?w=600&h=400&fit=crop"
                  alt="Enterprise Solutions"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                  Why Choose Our Enterprise Solutions?
                </Typography>
                <List>
                  {benefits.map((benefit, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: theme.palette.success.main }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card sx={{ p: 4, bgcolor: theme.palette.primary.main, color: 'white' }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold">
                    Ready to Transform Your Enterprise?
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ opacity: 0.9 }}>
                    Schedule a consultation with our enterprise team to discuss your specific requirements.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/contact')}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      '&:hover': { bgcolor: 'white', transform: 'translateY(-2px)' },
                    }}
                  >
                    Contact Enterprise Team
                  </Button>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Transform Your Enterprise" 
        subtitle="Get a custom solution designed for your global operations"
        buttonText="Request Consultation"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default EnterpriseSolutions;